import React, { useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@clerk/clerk-react";

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../@/components/ui/dialog";
import { Button } from "../../@/components/ui/button";
import { api_endpoint } from "@/constants/constant";
import { ClipLoader } from "react-spinners";

export default function BatchDeleteStudentCohortEnrollment({
  open,
  onOpenChange,
  selectedEnrollments,
  onDeleted,
  refetchEnrolments,
}) {
  const { getToken } = useAuth();
  const [loading, setLoading] = useState(false);

  const normalizedEnrollments = useMemo(() => {
    const rows = Array.isArray(selectedEnrollments) ? selectedEnrollments : [];
    return rows
      .map((e) => ({
        user_id: e?.user_id ?? e?.userId,
        cohort_id: e?.cohort_id ?? e?.cohortId,
        course_id: e?.course_id ?? e?.courseId,
      }))
      .filter(
        (e) =>
          Boolean(String(e.user_id ?? "").trim()) &&
          e.cohort_id != null &&
          e.course_id != null
      )
      .map((e) => ({
        user_id: String(e.user_id),
        cohort_id: Number(e.cohort_id),
        course_id: Number(e.course_id),
      }));
  }, [selectedEnrollments]);

  const canSubmit = normalizedEnrollments.length > 0;

  const handleDelete = async () => {
    if (!canSubmit) {
      toast.error("Select at least one valid enrollment.");
      return;
    }

    setLoading(true);
    try {
      const token = await getToken();
      if (!token) return;

      const payload = { enrollments: normalizedEnrollments };

      const reqConfig = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      };

      const res = await axios.delete(
        `${api_endpoint}/cohort/batch-delete-enrollments`,
        reqConfig
      );

      const data = res?.data;
      const totalDeleted =
        data?.total_deleted != null ? Number(data.total_deleted) : null;
      const totalErrors =
        data?.total_errors != null ? Number(data.total_errors) : null;

      const deletedCount = Number.isFinite(totalDeleted)
        ? totalDeleted
        : normalizedEnrollments.length;

      if (Number.isFinite(totalErrors) && totalErrors > 0) {
        toast.success(
          `Deleted ${deletedCount} enrollment${deletedCount === 1 ? "" : "s"}. ${
            totalErrors
          } failed.`
        );
      } else {
        toast.success(
          `Deleted ${deletedCount} enrollment${
            deletedCount === 1 ? "" : "s"
          } successfully!`
        );
      }

      onOpenChange(false);
      onDeleted?.(data);
      // Defer so parent refetch isn't affected by dialog state batching.
      if (refetchEnrolments) setTimeout(() => refetchEnrolments(), 0);
    } catch (error) {
      console.error("Error batch-deleting cohort enrolments:", error);
      toast.error("Failed to delete cohort enrolments.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md bg-(--surface-panel) text-(--neutral-900)">
        <DialogHeader>
          <DialogTitle className="font-heading text-(--neutral-900)">
            Delete Cohort Enrolments
          </DialogTitle>
        </DialogHeader>

        <div className="font-body text-(--neutral-800)">
          Are you sure you want to delete <b>{normalizedEnrollments.length}</b>{" "}
          cohort enrolment{normalizedEnrollments.length === 1 ? "" : "s"}? This
          action cannot be undone.
        </div>

        <div className="mt-4 flex gap-2 justify-between">
          <DialogClose asChild>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button
            variant=""
            onClick={handleDelete}
            disabled={loading || !canSubmit}
          >
            {loading ? (
              <span className="flex items-center justify-center w-full">
                <ClipLoader size={20} color="#fff" />
              </span>
            ) : (
              "Delete"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
